import { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CryptoJS from 'crypto-js';
import api from '../axiosConfig';

const AuthContext = createContext();

/**
 * FIXED SECRET KEY LOGIC
 * In Create React App, we use process.env.
 * The fallback ensures it works even if the .env file isn't set up yet.
 */
const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || 'Coders#147#';

// --- ENCRYPTION LOGIC ---
const encryptData = (data) => {
  if (!data) return null;
  try {
    const stringData = JSON.stringify(data);
    return CryptoJS.AES.encrypt(stringData, SECRET_KEY).toString();
  } catch (e) {
    console.error("Encryption error:", e);
    return null;
  }
};

const decryptData = (ciphertext) => {
  if (!ciphertext) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    
    // If decryption fails due to wrong key, decryptedString will be empty
    if (!decryptedString) return null;
    
    return JSON.parse(decryptedString);
  } catch (e) {
    console.error("Decryption error:", e);
    return null;
  }
};

// --- PROVIDER COMPONENT ---
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [userData, setUserData] = useState({});

  useEffect(() => {
    const storedToken = sessionStorage.getItem('token');
    const encUser = sessionStorage.getItem('user');
    const encUserData = sessionStorage.getItem('userData');

    if (storedToken) {
      setToken(storedToken);
      // Decrypt stored strings back into state
      const decryptedUser = decryptData(encUser);
      const decryptedUserData = decryptData(encUserData);

      if (decryptedUser) setUser(decryptedUser);
      if (decryptedUserData) setUserData(decryptedUserData);
    }
  }, []);

  const login = async (formData) => {
    try {
      const response = await api.post('/login', formData);
      const resData = response.data;
  
      if (resData.success_type === 'success' || resData?.message_type === 'success') {
        const userSlug = resData?.data?.role[0]?.slug;
        const tokenValue = resData.token;
        const data = resData?.data;

        if (data?.role?.[0]?.name === "Doctor") {
          data.name = `Dr. ${data?.name}`;
        }

        // Store Encrypted in Session Storage
        sessionStorage.setItem('token', tokenValue);
        sessionStorage.setItem('user', encryptData(userSlug));
        sessionStorage.setItem('userData', encryptData(data));
  
        // Set React State
        setUserData(data);
        setUser(userSlug);
        setToken(tokenValue);
  
        return userSlug;
      } else {
        throw new Error(resData.message || 'Login failed');
      }
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Login error');
    }
  };

  const logout = () => {
    sessionStorage.clear();
    setUser(null);
    setToken(null);
    setUserData({});
  };

  const isAuthenticated = !!user && !!token;

  return (
    <AuthContext.Provider value={{ user, userData, token, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// --- CUSTOM HOOK ---
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;}